import { ApplicationConfig, APP_INITIALIZER, importProvidersFrom, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { providePrimeNG } from 'primeng/config';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import Aura from '@primeng/themes/aura';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { MessageService } from 'primeng/api';
import { APP_NAME } from './core/tokens/app-name.token';
import { RecaptchaV3Module } from "ng-recaptcha";
import { MixpanelService } from './core/services/analytics/mixpanel.service';
import { MissingTranslationHandler, provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { translateHttpLoaderFactory } from './core/i18n/translate-loader.factory';
import { EsFallbackMissingTranslationHandler } from './core/i18n/missing-translation.handler';
import { LanguageService } from './core/services/language/language.service';
import { AbstractSecurityStorage, provideAuth } from 'angular-auth-oidc-client';
import { realmAuthConfig } from './core/auth/realm-auth.config';
import { InMemoryStorage } from './core/auth/in-memory-storage';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: APP_NAME, useValue: 'Veflo Vault' },
    importProvidersFrom(RecaptchaV3Module),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([
        authInterceptor,
        errorInterceptor,
      ])
    ),
    MessageService,
    provideAnimationsAsync(),
    provideAuth(realmAuthConfig),
    // DESPUÉS de provideAuth(...) a propósito: provideAuth registra DefaultSessionStorageService y
    // el último provider gana, así que este es el que decide dónde vive el estado OIDC. Sin él, el
    // blob por config —access token Y refresh token— queda escrito en sessionStorage.
    { provide: AbstractSecurityStorage, useClass: InMemoryStorage },
    provideTranslateService({
      defaultLanguage: 'es',
      useDefaultLang: true,
      loader: {
        provide: TranslateLoader,
        useFactory: translateHttpLoaderFactory,
        deps: [HttpClient],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: EsFallbackMissingTranslationHandler,
      },
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: (mp: MixpanelService) => () => mp.init(),
      deps: [MixpanelService],
      multi: true,
    },
    {
      // Aplica el idioma inicial para el primer pintado pre-sesión (localStorage → es)
      // SIN persistir (ADR-004). Si existe sesión, el effect() de LanguageService aplica
      // el idioma autoritativo del backend por encima de este valor.
      provide: APP_INITIALIZER,
      useFactory: (lang: LanguageService) => () => lang.applyInitial(),
      deps: [LanguageService],
      multi: true,
    },
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: false || 'none'
        },
      },
      translation: {
        dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
        dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
        dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
        monthNames: [
          'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
          'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
        ],
        monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
        today: 'Hoy',
        clear: 'Limpiar',
        dateFormat: 'dd/mm/yy',
        firstDayOfWeek: 1,
      },
    }),
  ]
};
